import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAndroidBackClose } from '../android/useAndroidBackClose';
import { getRecipes } from '../api/inventory';
import { getStores } from '../api/stores';
import { useAuth } from '../auth/AuthProvider';
import { ProductHeader } from '../components/product/ProductParts';
import type { RecipesState } from '../components/product/ProductSteps';
import { ProductWizard } from '../components/product/ProductWizard';
import { scrollParentOf } from '../components/pos/scrollParent';
import { Button, EmptyPanel, ErrorPanel, LoadingPanel } from '../components/ui/primitives';
import type { Recipe } from '../types/inventory';
import { recipeChoices, type StoreOption } from '../utils/newProduct';
import { leaveNewProduct, markNewProductEntry, watchBrowserBack } from '../utils/newProductRoute';

/**
 * Rediseño · Bloque E — la pantalla de `#nuevo-producto` (P-029). Llega aquí quien ya pasó el guard de
 * la ruta (`canCreateProduct`: OWNER o MANAGER); el EMPLOYEE ve «sin acceso» sin que se pida nada.
 *
 * Antes de mostrar el asistente se traen las tiendas en las que la persona puede vender (las mismas
 * que el alta de siempre: `GET /api/stores`, que a un MANAGER solo le devuelve las suyas). Un MANAGER
 * sin tiendas no puede crear nada —el servidor rechaza tanto la lista vacía como cualquier tienda—, así
 * que en vez del asistente se le dice por qué. Las recetas se piden a la vez y llegan cuando llegan:
 * solo hacen falta en el cuarto paso.
 */
export function NuevoProductoPage({ arrivedInApp }: { arrivedInApp: boolean }) {
  const { user } = useAuth();
  const role = user?.role;
  const [stores, setStores] = useState<StoreOption[] | 'loading' | 'error'>('loading');
  const [recipes, setRecipes] = useState<Recipe[] | 'loading' | 'error'>('loading');
  const rootRef = useRef<HTMLDivElement>(null);
  const alive = useRef(true);
  const wizardBack = useRef<(() => void) | null>(null);

  // La entrada del historial dice de dónde se llegó, para que «Atrás» y «Volver» regresen ahí.
  useLayoutEffect(() => { markNewProductEntry(arrivedInApp); }, [arrivedInApp]);

  // Empieza arriba y deja la vista arriba al irse: el shell solo lo hace cuando cambia el destino que
  // resalta, y aquí y en Ventas Otros es el mismo.
  useEffect(() => {
    alive.current = true;
    const scroller = rootRef.current ? scrollParentOf(rootRef.current) : null;
    if (scroller) scroller.scrollTop = 0;
    return () => {
      alive.current = false;
      if (scroller) scroller.scrollTop = 0;
    };
  }, []);

  const loadStores = useCallback(() => {
    setStores('loading');
    getStores()
      .then((result) => { if (alive.current) setStores(result.stores.map((store) => ({ id: store.id, name: store.name, isActive: store.isActive }))); })
      .catch(() => { if (alive.current) setStores('error'); });
  }, []);

  const loadRecipes = useCallback(() => {
    setRecipes('loading');
    getRecipes()
      .then((result) => { if (alive.current) setRecipes(result.recipes); })
      .catch(() => { if (alive.current) setRecipes('error'); });
  }, []);

  useEffect(() => { loadStores(); loadRecipes(); }, [loadStores, loadRecipes]);

  // El Atrás del teléfono se registra UNA vez, al abrir la pantalla, y no cuando llega el asistente: así
  // responde también mientras cargan las tiendas, si fallan o si un gerente no tiene ninguna (sale, como
  // «Atrás»), y lo que se abra encima mientras tanto —el menú— sigue encima. Con el asistente, hace lo
  // que él diga. Lo encontró la revisión independiente.
  useAndroidBackClose(() => { if (wizardBack.current) wizardBack.current(); else leaveNewProduct(); });

  // FI-93 (P-074): el Atrás del NAVEGADOR (la web abierta en el teléfono) hace lo mismo que el del teléfono, y
  // también desde que se abre la pantalla: mientras cargan las tiendas, si fallan o sin tiendas, sale como
  // «Atrás». Así una guarda que ya estaba (una recarga a medio asistente) nunca deja un Atrás sin efecto.
  // Ver `watchBrowserBack`. Lo encontró la revisión independiente de P-074 (R-01).
  useEffect(() => watchBrowserBack(() => { if (wizardBack.current) wizardBack.current(); else leaveNewProduct(); }), []);

  const recipesState: RecipesState = recipes === 'loading' ? { status: 'loading' }
    : recipes === 'error' ? { status: 'error' }
    : { status: 'ready', choices: recipeChoices(recipes) };

  let body;
  if (stores === 'loading') {
    body = <LoadingPanel label="Cargando tus tiendas…" lines={4} />;
  } else if (stores === 'error') {
    body = <ErrorPanel description="Revisa tu conexión e inténtalo de nuevo." onRetry={loadStores} title="No pudimos traer tus tiendas." />;
  } else if (role !== 'OWNER' && stores.length === 0) {
    body = (
      <EmptyPanel
        action={<Button onClick={leaveNewProduct} variant="secondary">Volver</Button>}
        description="Para crear un producto necesitas al menos una tienda asignada. Pídesela al dueño del negocio."
        icon="stores"
        title="No tienes tiendas asignadas"
      />
    );
  }

  return (
    <div ref={rootRef}>
      {body
        ? (
          <section aria-labelledby="product-title" className="n3-product">
            <ProductHeader onBack={leaveNewProduct} />
            <div className="n3-product__stack">{body}</div>
          </section>
        )
        : (
          <ProductWizard
            onExit={leaveNewProduct}
            onRetryRecipes={loadRecipes}
            phoneBack={wizardBack}
            recipeList={Array.isArray(recipes) ? recipes : []}
            recipes={recipesState}
            role={role}
            stores={stores as StoreOption[]}
          />
        )}
    </div>
  );
}
